import { useCallback, useEffect, useId, useMemo, useRef, useState, type DragEvent } from "react";
import { Button } from "../../../../components/Button";
import { Checkbox } from "../../../../components/Checkbox";
import { DateTimePicker } from "../../../../components/DateTimePicker";
import { Radio, RadioGroup } from "../../../../components/Radio";
import { SelectPopover } from "../../../../components/SelectPopover";
import { TextInput } from "../../../../components/TextInput";
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { ApiError } from "../../../../services/api";
import {
  confirmBulkCalendarEvents,
  getBulkCalendarEventsReview,
  getScheduleIngestionStatus,
  startScheduleIngestion,
  uploadBulkEventFiles,
} from "../../../../services/schedule-ingestions";
import {
  browserTimeZone,
  utcToZonedInput,
  zonedInputToUtcIso,
} from "../../../../utils/calendarTime";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import {
  DEFAULT_START_TIME,
  confirmBlockedReason,
  draftNeedsReview,
  includedDrafts,
  composeEventTitle,
  toConfirmPayload,
  toDrafts,
  type BulkEventDraft,
} from "./bulkEventRows";
import { FRIENDLY_FALLBACK, validateScheduleFile } from "./scheduleIngestionShared";
import { US_TIME_ZONES } from "./scheduleReview";
import "./BulkEventImportModal.css";

interface BulkEventImportModalProps {
  onClose: () => void;
  /** Fired once events land, so the host can refresh the calendar behind the modal. */
  onCreated?: () => void;
}

/** The server's re-confirm guard. A 409 here means the events already landed. */
function isAlreadyConfirmed(err: unknown): boolean {
  return err instanceof ApiError && err.statusCode === 409;
}

type Phase = "picking" | "uploading" | "reading" | "review" | "empty" | "failed" | "success";

const ACCEPT = ".pdf,image/*";

/**
 * Whether this browser can actually resolve an IANA id.
 *
 * The suggestion arrives over the wire, and an id `Intl` rejects would throw on the first attempt
 * to render a time — putting the whole review into a state where nothing reads correctly.
 */
const canResolveTimeZone = (id: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: id });
    return true;
  } catch {
    return false;
  }
};
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;
const STATUS_REQUEST_TIMEOUT_MS = 10000;
// The review fetch needs its own bound. Without one, a request that HANGS rather than fails leaves
// the flow with nothing pending and no deadline to reach.
const REVIEW_REQUEST_TIMEOUT_MS = 20000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

/**
 * Bulk-add individual events from a schedule the coach already holds.
 *
 * <p>QA: "they want to be able to add individual events to their own personal calendar OR in some
 * cases their organization calendar. They usually have an image or a pdf to upload... use an LLM to
 * extract event information such as name, dates, and locations if possible. Then from that the
 * events individually pop up on their calendar."</p>
 *
 * <p>Three things about this are deliberate:</p>
 *
 * <ul>
 *   <li><b>Nothing lands without review.</b> The model reads pixels off a photographed printout, so
 *   a confident wrong date is a normal outcome. Every row is editable, and the coach unticks what
 *   the model invented before anything reaches the calendar.</li>
 *
 *   <li><b>"My calendar" is an assignee lens, not privacy.</b> Every calendar event in RosterPilot
 *   is org-scoped and org-visible; there is no per-user visibility column, and adding one would
 *   change every read path in the app. The choice below assigns the coach to each event, which is
 *   what puts it under the schedule's existing "My events" filter. The copy says so rather than
 *   implying a private calendar that does not exist.</li>
 *
 *   <li><b>Design-system controls only.</b> QA: "the UI should actually be nice for this, no
 *   regular html componenets or anything like that, use the design system within the app itself".
 *   `scheduleControlPrimitives.test.tsx` holds that at the source level, because a raw
 *   `&lt;button&gt;` renders and passes every rendering test.</li>
 * </ul>
 */
export function BulkEventImportModal({ onClose, onCreated }: BulkEventImportModalProps) {
  const [phase, setPhase] = useState<Phase>("picking");
  const [error, setError] = useState("");
  const [ingestionId, setIngestionId] = useState("");
  const [drafts, setDrafts] = useState<BulkEventDraft[]>([]);
  /*
    Defaults to the ORGANIZATION calendar, not the coach's own.

    A schedule someone photographed and imported is almost always the program's, not one person's
    — and the wrong default here is not symmetric: events landing unassigned are visible to
    everyone and easy to claim, whereas events silently assigned to whoever did the import are
    filtered out of the view their colleagues are looking at.
  */
  const [assignToMe, setAssignToMe] = useState(false);
  /**
   * Whose schedule this is. A screenshot of a printed schedule very often loses the header, so the
   * rows read "vs Central" with no sign of which team is playing them — and the event that lands
   * on the calendar then names one team, leaving nobody able to tell which side is ours.
   *
   * Optional, and applied at confirm rather than as the coach types, so each row keeps saying what
   * the document said. That is what they are comparing against the paper in front of them.
   */
  const [teamName, setTeamName] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLButtonElement>(null);
  // One controller for the whole create -> upload -> start -> poll run, so closing the modal aborts
  // every request still in flight. (The GCS PUTs themselves are not cancellable — uploadToGCS takes
  // no signal — so those finish in the background; nothing they produce reaches the screen.)
  const runControllerRef = useRef<AbortController | null>(null);
  // The confirm gets its own, because it outlives the run: it is the one step that writes, and
  // abandoning it silently is the failure worth guarding.
  const confirmControllerRef = useRef<AbortController | null>(null);
  // Read from event handlers registered once, which would otherwise close over the initial `false`.
  const confirmingRef = useRef(false);

  /*
    The coach's device zone, and the default. A schedule almost never states a timezone, so this
    is a guess — a good one when they are importing their own region's schedule and wrong exactly
    when they are importing another region's.
  */
  const deviceTimeZone = useMemo(() => browserTimeZone(), []);
  const [timeZone, setTimeZone] = useState(deviceTimeZone);
  /*
    Whether the coach PICKED this zone or it is just their device's.

    The server treats the two differently, and it has to: a choice beats a venue lookup, because a
    lookup that overruled it would silently discard what they said. A default does not — the venue
    is better evidence than the location of the laptop.
  */
  const [timeZoneChosen, setTimeZoneChosen] = useState(false);
  const timeZoneId = useId();

  /** Whether the default came from the schedule's own locations or from this device. */
  const [zoneFromSchedule, setZoneFromSchedule] = useState(false);

  /*
    The eight US zones the schedule review already offers, plus the device's own when it is not
    one of them — a coach abroad, or a zone we have not listed, must still see what is actually
    going to be used rather than a list that silently excludes it.
  */
  const zoneOptions = useMemo(() => {
    const known = US_TIME_ZONES.map((z) => ({ id: z.id, label: z.label }));
    if (known.some((z) => z.id === deviceTimeZone)) return known;
    return [...known, { id: deviceTimeZone, label: `${deviceTimeZone} (this device)` }];
  }, [deviceTimeZone]);

  const timeZoneOptions = useMemo(() => zoneOptions.map((z) => z.label), [zoneOptions]);
  const timeZoneLabel =
    zoneOptions.find((z) => z.id === timeZone)?.label ?? zoneOptions[0]?.label ?? "";

  /** The first kept row, named the way the confirm will name it. */
  const titlePreview = useMemo(() => {
    const first = includedDrafts(drafts).find((d) => d.title.trim() !== "");
    return first ? composeEventTitle(first.title, teamName) : "";
  }, [drafts, teamName]);

  // handleEscape: false, and Escape handled below on the BUBBLE phase.
  //
  // useDialogFocus listens on `document` in the CAPTURE phase, so it fires before any React handler
  // and DateTimePicker's own Escape-to-close-the-panel stopPropagation never gets to run. With the
  // default, pressing Escape to dismiss a row's calendar unmounted the whole modal and threw away
  // every edit the coach had made. CallListBuilderModal and RescheduleCallModal — the two other
  // dialogs hosting a DateTimePicker — already do exactly this.
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false, lockScroll: true });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Not while a write is in flight: dismissing then would read as "cancelled" while the events
      // land anyway. See closeIfIdle.
      if (event.key !== "Escape" || confirmingRef.current) return;
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(
    () => () => {
      runControllerRef.current?.abort();
      confirmControllerRef.current?.abort();
    },
    [],
  );

  /*
    A file dropped anywhere in the dialog OTHER than the zone. The browser's default is to navigate
    to it, which from here means abandoning the picker for whatever the coach was doing.

    Scoped to the picking phase: the zone (and its ref) only exists then, and once a schedule is
    uploaded a stray drop over the review rows should not be reinterpreted as a new one.

    `globalThis.DragEvent` because React's DragEvent type is imported into this file and shadows
    the DOM one — this handler receives the native event, not the synthetic one.
  */
  useEffect(() => {
    if (phase !== "picking") return;

    const allowDrop = (e: globalThis.DragEvent) => e.preventDefault();

    const handleStrayDrop = (e: globalThis.DragEvent) => {
      e.preventDefault();

      // `window` is a legitimate target here and is not a Node, so it cannot be passed to
      // `contains` — which throws rather than returning false.
      const target = e.target instanceof Node ? e.target : null;
      if (target && dropzoneRef.current?.contains(target)) return;

      if (!e.dataTransfer || e.dataTransfer.files.length === 0) return;

      // A miss that is still inside the dialog is told where to aim, rather than silently
      // accepted — several files landing an inch wide of the box should not start a run the
      // coach did not mean to start.
      if (target && dialogRef.current?.contains(target)) {
        setError("Drop the file on the box above.");
      }
    };

    window.addEventListener("dragover", allowDrop);
    window.addEventListener("drop", handleStrayDrop);
    return () => {
      window.removeEventListener("dragover", allowDrop);
      window.removeEventListener("drop", handleStrayDrop);
    };
  }, [phase]);

  const patchDraft = useCallback((key: string, patch: Partial<BulkEventDraft>) => {
    setDrafts((rows) =>
      rows.map((row) => {
        if (row.key !== key) return row;
        const next = { ...row, ...patch };
        // Recomputed from the merged row, not cleared outright. Editing a row IS the coach settling
        // it — but a blanket clear meant fixing the title of a row whose date could not be read
        // removed the badge while the date was still empty, leaving a 40-row list with a disabled
        // button, a message naming no row, and nothing pointing at the one that needs work.
        return { ...next, needsReview: draftNeedsReview(next) };
      }),
    );
  }, []);

  /**
   * Resolves with "completed" when the extraction finished, or "failed" when the loop already put a
   * message on screen. Deliberately does NOT fetch the events: a failure there is permanent (a 400
   * for the wrong kind, a parse error) and inside the loop it was counted as a network blip,
   * retried five times, and finally reported as "check your connection" — with a working
   * connection. Its own lack of a per-request timeout also meant a hung fetch never rescheduled a
   * tick, so the 10-minute deadline could not fire and the spinner ran forever.
   */
  const pollUntilDone = (id: string, signal: AbortSignal) =>
    new Promise<"completed" | "failed" | "aborted">((resolve) => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let consecutiveFailures = 0;

      const poll = async () => {
        if (signal.aborted) return resolve("aborted");

        if (Date.now() > deadline) {
          setError("This is taking longer than expected. Close this and try again.");
          setPhase("failed");
          return resolve("failed");
        }

        try {
          const status = await getScheduleIngestionStatus(id, signal, STATUS_REQUEST_TIMEOUT_MS);
          if (signal.aborted) return resolve("aborted");
          consecutiveFailures = 0;

          if (status.status === "Completed") {
            return resolve("completed");
          }

          if (status.status === "Failed") {
            setError(status.error || FRIENDLY_FALLBACK);
            setPhase("failed");
            return resolve("failed");
          }
        } catch {
          if (signal.aborted) return resolve("aborted");
          // A single failed poll is a network blip; the next tick retries. A run of them is a
          // connection problem, and saying so beats an indefinite spinner.
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
            setError(
              "We are having trouble reaching the server. Check your connection and try again.",
            );
            setPhase("failed");
            return resolve("failed");
          }
        }

        setTimeout(poll, POLL_INTERVAL_MS);
      };

      void poll();
    });

  const handleFiles = async (files: File[]) => {
    setError("");
    if (files.length === 0) return;

    for (const file of files) {
      const validationError = validateScheduleFile(file);
      if (validationError) {
        setError(files.length === 1 ? validationError : `${file.name}: ${validationError}`);
        setPhase("failed");
        return;
      }
    }

    runControllerRef.current?.abort();
    const controller = new AbortController();
    runControllerRef.current = controller;
    const { signal } = controller;

    try {
      setPhase("uploading");
      const newIngestionId = await uploadBulkEventFiles(files, signal);
      if (signal.aborted) return;
      setIngestionId(newIngestionId);

      setPhase("reading");
      await startScheduleIngestion(newIngestionId, signal);
      if (signal.aborted) return;

      const outcome = await pollUntilDone(newIngestionId, signal);
      if (outcome !== "completed") return;

      // The status poll carries GAMES, which a bulk-events ingestion never has, so the events come
      // from their own endpoint. Out here, a failure lands in the catch below and the coach reads
      // the server's actual message.
      const review = await getBulkCalendarEventsReview(
        newIngestionId, signal, REVIEW_REQUEST_TIMEOUT_MS);
      if (signal.aborted) return;

      // Confirmed already — a reused ingestion id, or a confirm whose response the coach never saw.
      // Showing the review again would invite a submit the server answers with a 409.
      if (review.alreadyConfirmed) {
        setCreatedCount(review.events.length);
        setPhase("success");
        return;
      }

      /*
        The server's suggestion becomes the picker's default, when it gave one and it is a zone
        this browser can actually resolve. Validated rather than trusted: an id we cannot render
        would put the whole review into a state where every time reads wrong.

        Still not "chosen" — the coach has not touched it, so `TimezoneAssumed` stays true. It is a
        better guess than the device zone, not a statement.
      */
      const suggested = review.suggestedTimeZone;
      if (suggested && canResolveTimeZone(suggested)) {
        setTimeZone(suggested);
        setZoneFromSchedule(true);
      }

      const rows = toDrafts(review.events, new Date());
      setDrafts(rows);
      setPhase(rows.length === 0 ? "empty" : "review");
    } catch (err) {
      if (signal.aborted) return;
      setError(getSafeErrorMessage(err, FRIENDLY_FALLBACK));
      setPhase("failed");
    }
  };

  const onPickChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset so picking the same file again re-triggers change.
    e.target.value = "";
    if (files.length > 0) void handleFiles(files);
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    // `dragover` has to preventDefault on EVERY fire or the browser refuses the drop and navigates
    // to the file instead; `dragenter` shares the handler only so the highlight appears at once.
    e.preventDefault();

    // Text and images dragged from another tab are not a file. Highlighting for them promises a
    // drop that would do nothing.
    if (!e.dataTransfer.types.includes("Files")) return;

    // Tells the cursor this is a copy, not a move or a link — otherwise the browser guesses.
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLButtonElement>) => {
    // Only when the pointer actually leaves the zone. The zone has children (the icon, the text),
    // so a bare dragleave fires every time the cursor crosses between them and the highlight
    // strobes.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(false);

    // Same path a picked file takes — handleFiles already validates every file with
    // validateScheduleFile and reads several pages of one schedule as one upload, so a dropped
    // file and a picked file cannot diverge.
    const files = Array.from(e.dataTransfer.files);
    void handleFiles(files);
  };

  const handleRetry = () => {
    setError("");
    setIngestionId("");
    setDrafts([]);
    setPhase("picking");
  };

  const blockedReason = confirmBlockedReason(drafts);
  const keptCount = includedDrafts(drafts).length;

  const handleConfirm = async () => {
    if (blockedReason || confirming) return;
    setError("");
    setConfirming(true);
    confirmingRef.current = true;

    const controller = new AbortController();
    confirmControllerRef.current = controller;
    const { signal } = controller;

    const named = drafts.map((draft) => ({
      ...draft,
      title: composeEventTitle(draft.title, teamName),
    }));
    const events = toConfirmPayload(named, timeZone);
    try {
      const response = await confirmBulkCalendarEvents(
        ingestionId,
        { events, timeZone, timeZoneExplicit: timeZoneChosen, assignToMe },
        signal,
      );
      if (signal.aborted) return;
      setCreatedCount(response.eventsCreated);
      setPhase("success");
      onCreated?.();
    } catch (err) {
      if (signal.aborted) return;
      // A 409 means the events ARE on the calendar — the server's re-confirm guard fired because
      // this request was a retry, or because the first response was lost on the way back. Reporting
      // it as a failure is how a coach ends up re-uploading and duplicating a whole schedule by
      // hand, so it is the one error that is really a success.
      if (isAlreadyConfirmed(err)) {
        setCreatedCount(events.length);
        setPhase("success");
        onCreated?.();
        return;
      }
      setError(getSafeErrorMessage(err, "We couldn't add those events. Try again."));
    } finally {
      setConfirming(false);
      confirmingRef.current = false;
    }
  };

  /**
   * Dismissal, but never mid-write. Closing during the confirm reads as "cancelled" while the
   * request completes anyway — the events land, `onCreated` fires on the still-mounted calendar,
   * and the coach is never told. Blocking is the honest option: the button says what is happening.
   */
  const closeIfIdle = () => {
    if (confirming) return;
    onClose();
  };

  /**
   * The row's wall clock as an instant, for the picker. `zonedInputToUtcIso` is the same converter
   * the confirm uses, so what the coach sees in the picker is what gets sent.
   */
  const rowInstant = (date: string, time: string): string | null =>
    date && time ? zonedInputToUtcIso(`${date}T${time}`, timeZone) : null;

  /**
   * Read a picked instant back into the row's `YYYY-MM-DD` + `HH:mm` fields, through the exact
   * inverse of the converter `rowInstant` uses — so what the coach sees in the picker is what gets
   * sent, by construction rather than by coincidence.
   */
  const partsOf = (iso: string) => {
    const wall = utcToZonedInput(iso, timeZone);
    if (!wall) return null;
    return { date: wall.slice(0, 10), time: wall.slice(11, 16) };
  };

  return (
    <div
      className="bulk-events-overlay"
      // Presentational: clicking the dim backdrop is a convenience duplicate of the header's own
      // close button, which is the real focusable, keyboard-reachable affordance — and Escape
      // closes too. mousedown with a target check rather than a click handler on the whole
      // overlay, so a drag that starts inside the dialog and ends on the backdrop does not
      // dismiss it mid-edit. Same pattern as the itinerary catalog's overlay.
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeIfIdle();
      }}
      data-testid="bulk-events-overlay"
    >
      <div
        className="bulk-events-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-events-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="bulk-events__head">
          <div>
            <h2 id="bulk-events-title" className="bulk-events__title">
              Bulk add events
            </h2>
            <p className="bulk-events__subtitle">
              Upload a schedule and we'll pull the events out of it.
            </p>
          </div>
          {/* The header's dismiss is an icon action, not a peer of the page's primary buttons —
              the same pattern the other schedule panels use for their close ×. */}
          <Button
            type="button"
            variant="secondary"
            className="bulk-events__close"
            aria-label="Close"
            disabled={confirming}
            onClick={closeIfIdle}
            data-testid="bulk-events-close"
          >
            ✕
          </Button>
        </div>

        <div className="bulk-events__body">
          {phase === "picking" && (
            <div className="bulk-events__picker">
              <p className="bulk-events__lead">
                A season sheet, a camp flyer, a photo of a printout — PDF, PNG or JPG. Pick several
                to read them as one schedule.
              </p>
              {error && (
                <p className="bulk-events__error" role="alert" data-testid="bulk-events-picker-error">
                  {error}
                </p>
              )}
              {/*
                The zone IS the Button: scheduleControlPrimitives.test.tsx forbids a raw HTML
                button element in this file, so the dashed drop box has to be the design-system
                primitive rather than a hand-rolled wrapper around it. ButtonHTMLAttributes already
                forwards onDragOver/onDragEnter/onDragLeave/onDrop through Button's {...props}
                spread; only `ref` needed adding there, since a plain function component does not
                forward one on its own.
              */}
              <Button
                type="button"
                variant="secondary"
                className={`bulk-events__dropzone${
                  isDragging ? " bulk-events__dropzone--dragging" : ""
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                ref={dropzoneRef}
                data-testid="bulk-events-pick"
              >
                <svg
                  className="bulk-events__dropzone-icon"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  {/*
                    An SVG, not "⬆". U+2B06 has Emoji_Presentation=Yes, so Windows, Android and
                    most Chrome installs render it through the emoji font as a blue-and-white tile
                    that ignores `color` entirely.
                  */}
                  <path
                    d="M12 16V4m0 0L7 9m5-5 5 5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Drag a PDF or image here, or click to choose
              </Button>
              {/*
                The one native element here, and unavoidable: a file picker cannot be opened
                without it, and the design system has no file-upload primitive. It is hidden and
                driven by the Button above, so what the coach sees and operates is the primitive.
              */}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                className="bulk-events__file-input"
                onChange={onPickChange}
                aria-hidden="true"
                tabIndex={-1}
                data-testid="bulk-events-file-input"
              />
              <p className="bulk-events__hint">
                Nothing is added to the calendar until you've checked it.
              </p>
            </div>
          )}

          {(phase === "uploading" || phase === "reading") && (
            <div className="bulk-events__progress" data-testid="bulk-events-progress">
              <div className="bulk-events__spinner" />
              <p className="bulk-events__phase">
                {phase === "uploading"
                  ? "Uploading your schedule..."
                  : "Reading the events off your schedule..."}
              </p>
              <p className="bulk-events__hint">This can take a minute.</p>
            </div>
          )}

          {phase === "failed" && (
            <div className="bulk-events__failed" data-testid="bulk-events-error">
              <p className="bulk-events__error" role="alert">
                {error}
              </p>
              <Button type="button" variant="primary" onClick={handleRetry}>
                Try again
              </Button>
            </div>
          )}

          {phase === "empty" && (
            <div className="bulk-events__failed" data-testid="bulk-events-empty">
              <p className="bulk-events__error">
                We didn't find any events in that file. Try a clearer PDF or screenshot.
              </p>
              <Button type="button" variant="primary" onClick={handleRetry}>
                Try another file
              </Button>
            </div>
          )}

          {phase === "review" && (
            <div className="bulk-events__review" data-testid="bulk-events-review">
              <p className="bulk-events__lead">
                {drafts.length} {drafts.length === 1 ? "event" : "events"} found. Check the details,
                untick anything you don't want, then add them.
              </p>

              {/*
                The preview underneath is produced by the same function the confirm will run, so
                there is nothing here for the coach to be surprised by later.
              */}
              <div className="bulk-events__team">
                <TextInput
                  label="Team this schedule is for"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Optional, e.g. 16U Navy"
                  data-testid="bulk-events-team"
                />
                <p className="bulk-events__hint">
                  {teamName.trim() && titlePreview
                    ? `Events will be named like "${titlePreview}"`
                    : "Added to each event name, so a row that only names an opponent still says who they played."}
                </p>
              </div>

              {/*
                Destination, timezone and the confirm all sit ABOVE the list, because that is where
                the coach asked for them. It also reads better on a long import: the three
                decisions that apply to the WHOLE batch are made once at the top, and the rows
                below are the per-event detail you scroll through afterwards. The confirm is not
                duplicated at the bottom — one button, in one place.
              */}
              <div className="bulk-events__batch">
              <RadioGroup
                label="Where should these go?"
                className="bulk-events__destination"
                testId="bulk-events-destination"
              >
                <Radio
                  name="bulk-events-destination"
                  checked={assignToMe}
                  onChange={() => setAssignToMe(true)}
                  label="My calendar"
                  hint="Assigns them to you, so they show under the schedule's My events filter."
                />
                <Radio
                  name="bulk-events-destination"
                  checked={!assignToMe}
                  onChange={() => setAssignToMe(false)}
                  label="Organization calendar"
                  hint="Unassigned, for anyone in your organization to pick up."
                />
              </RadioGroup>
              {/* Said plainly rather than implied. There is no private calendar in RosterPilot,
                  and letting the label suggest otherwise would be the more damaging kind of
                  wrong — a coach putting something on "their" calendar the whole org can read. */}
              <p className="bulk-events__hint">
                Either way, everyone in your organization can see these events.
              </p>

              {/*
                The times on a photographed schedule are local to wherever it was printed, and the
                document almost never says where that is. Defaulted to this device's zone, which is
                right when a coach imports their own region's schedule and wrong exactly when they
                import another region's — so it is a control, not a label.

                Touching it tells the server the zone was CHOSEN, which stops a venue lookup
                overruling it.
              */}
              <div className="bulk-events__timezone">
                <label className="bulk-events__label" htmlFor={timeZoneId}>
                  Times on this schedule are in
                </label>
                <SelectPopover
                  id={timeZoneId}
                  options={timeZoneOptions}
                  value={timeZoneLabel}
                  onChange={(label) => {
                    const picked = zoneOptions.find((z) => z.label === label);
                    if (!picked) return;
                    setTimeZone(picked.id);
                    setTimeZoneChosen(true);
                  }}
                  placeholder="Choose a timezone"
                  clearable={false}
                />
                {!timeZoneChosen && (
                  <p className="bulk-events__hint">
                    {zoneFromSchedule
                      ? "Worked out from the locations on your schedule. Change it if that is wrong."
                      : "Taken from this device. Change it if the schedule is for somewhere else."}
                  </p>
                )}
              </div>

                {/* Beside the button that produced it. Left under the list it would sit a whole
                    scroll away from the control the coach just pressed. */}
                {error && (
                  <p
                    className="bulk-events__error"
                    role="alert"
                    data-testid="bulk-events-confirm-error"
                  >
                    {error}
                  </p>
                )}

              <div className="bulk-events__actions">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={confirming}
                  onClick={handleRetry}
                >
                  Start over
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={blockedReason !== "" || confirming}
                  loading={confirming}
                  onClick={handleConfirm}
                  data-testid="bulk-events-confirm"
                >
                  {`Add ${keptCount} ${keptCount === 1 ? "event" : "events"}`}
                </Button>
              </div>
                {blockedReason && (
                  <p className="bulk-events__blocked" data-testid="bulk-events-blocked">
                    {blockedReason}
                  </p>
                )}
              </div>

              <ul className="bulk-events__rows">
                {drafts.map((draft) => (
                  <li
                    key={draft.key}
                    className={`bulk-events__row${
                      draft.include ? "" : " bulk-events__row--excluded"
                    }`}
                    data-testid="bulk-events-row"
                  >
                    <div className="bulk-events__row-lead">
                      <Checkbox
                        checked={draft.include}
                        onChange={(e) =>
                          setDrafts((rows) =>
                            rows.map((row) =>
                              row.key === draft.key
                                ? { ...row, include: e.target.checked }
                                : row,
                            ),
                          )
                        }
                        aria-label={`Add ${draft.title || "this event"}`}
                      />
                      {draft.needsReview && (
                        <span className="bulk-events__flag" data-testid="bulk-events-flag">
                          Check this
                        </span>
                      )}
                    </div>

                    <div className="bulk-events__row-fields">
                      <TextInput
                        label="Event"
                        value={draft.title}
                        onChange={(e) => patchDraft(draft.key, { title: e.target.value })}
                        placeholder="What is it?"
                        disabled={!draft.include}
                      />

                      <div className="bulk-events__row-when">
                        {/*
                          hideLabel with a row-specific label, not the bare "Starts". DateTimePicker
                          names its portalled panel from the label, so on a 30-event review a screen
                          reader would otherwise announce sixty identical controls with nothing to
                          tell row 3 from row 19 — the case the component's own doc comment warns
                          about. The visible column headers carry the sighted label.
                        */}
                        <DateTimePicker
                          label={`${draft.allDay ? "Date" : "Starts"} — ${
                            draft.title || "untitled event"
                          }`}
                          hideLabel
                          value={rowInstant(draft.date, draft.allDay ? "00:00" : draft.startTime)}
                          onChange={(iso) => {
                            const parts = partsOf(iso);
                            if (!parts) return;
                            patchDraft(draft.key, {
                              date: parts.date,
                              // An all-day row keeps its end on the same day; a timed one keeps
                              // whatever span the coach set.
                              ...(draft.allDay
                                ? { endDate: parts.date }
                                : {
                                    startTime: parts.time,
                                    ...(draft.endDate === draft.date
                                      ? { endDate: parts.date }
                                      : {}),
                                  }),
                            });
                          }}
                          disabled={!draft.include}
                          compact
                        />
                        {!draft.allDay && (
                          <DateTimePicker
                            label={`Ends — ${draft.title || "untitled event"}`}
                            hideLabel
                            value={rowInstant(draft.endDate || draft.date, draft.endTime)}
                            onChange={(iso) => {
                              const parts = partsOf(iso);
                              if (!parts) return;
                              // BOTH parts. This control offers a month grid, so dropping the date
                              // meant a coach picking a 1:00 AM next-day end watched the field snap
                              // back a day — the picker visibly refusing what they typed.
                              patchDraft(draft.key, {
                                endDate: parts.date,
                                endTime: parts.time,
                              });
                            }}
                            disabled={!draft.include}
                            compact
                          />
                        )}
                      </div>

                      <div className="bulk-events__row-extras">
                        <Checkbox
                          label="All day"
                          checked={draft.allDay}
                          disabled={!draft.include}
                          onChange={(e) =>
                            patchDraft(draft.key, {
                              allDay: e.target.checked,
                              endDate: draft.date,
                              // Unticking needs a time to show; the schedule gave none, so this is
                              // a starting point the coach adjusts, not a claim about the document.
                              startTime:
                                e.target.checked || draft.startTime
                                  ? draft.startTime
                                  : DEFAULT_START_TIME,
                            })
                          }
                        />
                        <TextInput
                          label="Where"
                          value={draft.location}
                          onChange={(e) => patchDraft(draft.key, { location: e.target.value })}
                          placeholder="Optional"
                          disabled={!draft.include}
                        />
                      </div>

                      {draft.allDay && (
                        <p className="bulk-events__row-hint">
                          All day — only the date is used.
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

            </div>
          )}

          {phase === "success" && (
            <div className="bulk-events__success" data-testid="bulk-events-success">
              <p className="bulk-events__phase">
                {createdCount} {createdCount === 1 ? "event is" : "events are"} on the calendar.
              </p>
              <Button type="button" variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
